import { Field, Float, ID, InputType, ObjectType, registerEnumType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity, generateEntityId, RelatedEntity, User } from '@ztechapps/common.contract'
import { AssessmentStatusEnum } from '../enums/AssessmentStatusEnum'
import { AssessmentTypeEnum } from '../enums/AssessmentTypeEnum'
import { AnswerKey } from './AnswerKey'

// Registered beside the fields that use them so the enum files stay decorator-free for the mobile apps.
registerEnumType(AssessmentStatusEnum, {
  name: 'AssessmentStatusEnum',
  description: 'Lifecycle status of an assessment',
})

registerEnumType(AssessmentTypeEnum, {
  name: 'AssessmentTypeEnum',
  description: 'Answer sheet type that determines which scan model/vendor grades the assessment',
})

@ObjectType('Assessment')
@InputType('AssessmentInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'assessments' } })
export class Assessment extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('asmt') })
  declare _id: string

  /**
   * The teacher who owns this assessment. Multi-user isolation is enforced by
   * matching this owner against the request context user (RLS parity).
   */
  @Field(() => User)
  @Prop({ type: RelatedEntity })
  owner: ReturnType<() => User>

  @Field(() => String)
  @Prop({ type: String, required: true })
  declare name: string

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  description?: string

  @Field(() => AssessmentStatusEnum)
  @Prop({ type: String, enum: AssessmentStatusEnum, required: true, default: AssessmentStatusEnum.Draft })
  declare status: AssessmentStatusEnum

  /**
   * Answer sheet type — determines which scan model/vendor grades this assessment
   * and, for Bubble, restricts the answer key to multiple-choice questions only.
   */
  @Field(() => AssessmentTypeEnum)
  @Prop({ type: String, enum: AssessmentTypeEnum, required: true, default: AssessmentTypeEnum.Handwritten })
  declare type: AssessmentTypeEnum

  @Field(() => Float)
  @Prop({ type: Number, default: 0 })
  totalQuestions: number

  /**
   * Single embedded answer key (may be created manually or by scanning a master sheet).
   * Functional prop type defers evaluation of the embedded class.
   */
  @Field(() => AnswerKey, { nullable: true })
  @Prop({ type: () => AnswerKey })
  answerKey?: ReturnType<() => AnswerKey>
}
